import { useState } from 'react';
import { Bell, Search, Check, LogOut } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import { useAuth } from '../context/AuthContext';
import logo from '../logo.png';
const s = {
  topbar: {
    height: 100,
    minHeight: 100,
    maxHeight: 100,
    overflow: 'hidden',
    background: 'linear-gradient(-125deg,#5f1e1e,#9b2c2c)',
    display: 'flex',
    alignItems: 'center',
    padding: '0 20px',
    gap: 0,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    position: 'sticky',
    top: 0,
    zIndex: 200,
    flexShrink: 0,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#fff',
    fontWeight: 800,
    fontSize: 17,
    letterSpacing: '-0.02em',
    marginRight: 16,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  divider: {
    width: 1,
    alignSelf: 'stretch',
    margin: '10px 0',
    background: 'rgba(255,255,255,0.15)',
    flexShrink: 0,
  },
  filterBlock: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 24px',
    flexShrink: 0,
    height: '100%',
  },
  filterLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.5)',
    marginRight: 12,
    whiteSpace: 'nowrap',
  },
  checkList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    maxHeight: 64,
    overflowY: 'scroll',
    paddingRight: 4,
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255,255,255,0.2) transparent',
  },
  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginBottom: 4,
    background: 'rgba(255,255,255,0.08)',
    borderRadius: 5,
    padding: '3px 7px',
  },
  searchInput: {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#fff',
    fontSize: 11,
    width: 90,
    '::placeholder': { color: 'rgba(255,255,255,0.4)' },
  },
  checkItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    userSelect: 'none',
    height: 20,
    flexShrink: 0,
  },
  checkBox: {
    width: 14,
    height: 14,
    borderRadius: 3,
    border: '1.5px solid rgba(255,255,255,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.15s',
    background: 'transparent',
  },
  checkBoxActive: {
    background: '#fc8181',
    border: '1.5px solid #fc8181',
  },
  checkLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.75)',
    whiteSpace: 'nowrap',
    transition: 'color 0.15s',
  },
  checkLabelActive: {
    color: '#fff',
  },
  spacer: { flex: 1 },
  iconBtn: {
    width: 34,
    height: 34,
    borderRadius: 8,
    border: 'none',
    background: 'rgba(255,255,255,0.12)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    marginLeft: 8,
    transition: 'background 0.15s',
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: 'linear-gradient(135deg,#48bb78,#38a169)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
    border: '2px solid rgba(255,255,255,0.3)',
    flexShrink: 0,
    marginLeft: 8,
  },
  badge: { position: 'relative', display: 'inline-flex', marginLeft: 8 },
  badgeDot: {
    position: 'absolute',
    top: -2, right: -2,
    width: 8, height: 8,
    borderRadius: '50%',
    background: '#fc8181',
    border: '2px solid #9b2c2c',
  },
};

function InlineCheckGroup({ label, items = [], selected = [], onChange, withSearch }) {
  const [q, setQ] = useState('');
  const toggle = key =>
    onChange(selected.includes(key) ? selected.filter(v => v !== key) : [...selected, key]);
  const visible = withSearch && q.trim()
    ? items.filter(o => o.value.toLowerCase().includes(q.toLowerCase()))
    : items;
  const allSelected = items.length > 0 && selected.length === items.length;
  const toggleAll = () => onChange(allSelected ? [] : items.map(o => o.key));

  return (
    <div style={s.filterBlock}>
      <span style={s.filterLabel}>{label}</span>
      <div>
        {withSearch && (
          <div style={s.searchWrap}>
            <Search size={10} color="rgba(255,255,255,0.45)" />
            <input
              style={s.searchInput}
              placeholder="Cari..."
              value={q}
              onChange={e => setQ(e.target.value)}
            />
          </div>
        )}
        <div style={s.checkList}>
          {!q.trim() && (
            <div
              style={{ ...s.checkItem, borderBottom: '1px solid rgba(255,255,255,0.12)', paddingBottom: 3, marginBottom: 1 }}
              onClick={toggleAll}
            >
              <div style={{ ...s.checkBox, ...(allSelected ? s.checkBoxActive : { border: '1.5px solid rgba(255,255,255,0.55)' }) }}>
                {allSelected && <Check size={9} color="#fff" strokeWidth={3} />}
              </div>
              <span style={{ ...s.checkLabel, fontWeight: 700, ...(allSelected ? s.checkLabelActive : { color: 'rgba(255,255,255,0.85)' }) }}>
                All
              </span>
            </div>
          )}
          {visible.map(({ key, value }) => {
            const active = selected.includes(key);
            return (
              <div key={key} style={s.checkItem} onClick={() => toggle(key)}>
                <div style={{ ...s.checkBox, ...(active ? s.checkBoxActive : {}) }}>
                  {active && <Check size={9} color="#fff" strokeWidth={3} />}
                </div>
                <span style={{ ...s.checkLabel, ...(active ? s.checkLabelActive : {}) }}>
                  {value}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Topbar() {
  const { filterOptions, filterState, setters } = useDashboard();
  const { sector, distributor, produk } = filterState;
  const { setSector, setDistributor, setProduk } = setters;
  const { user, logout } = useAuth();

  const initial = (user?.name || user?.id || '?').trim().charAt(0).toUpperCase();

  return (
    <header style={s.topbar}>
      <div style={s.logo}>
        <img style={{filter: 'brightness(0) invert(1)'}} src={logo} alt="Logo" />
      </div>

      <div style={s.divider} />

      {filterOptions && <>
        <InlineCheckGroup label="Sector"      items={filterOptions.sector}      selected={sector}      onChange={setSector} />
        <div style={s.divider} />
        <InlineCheckGroup label="Distributor" items={filterOptions.distributor} selected={distributor} onChange={setDistributor} withSearch />
        <div style={s.divider} />
        <InlineCheckGroup label="Produk"      items={filterOptions.produk}      selected={produk}      onChange={setProduk} withSearch />
      </>}

      <div style={s.spacer} />

      {/* <button
        style={s.iconBtn}
        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.22)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
      >
        <Search size={16} />
      </button>

      <div style={s.badge}>
        <button
          style={{ ...s.iconBtn, marginLeft: 0 }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.22)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
        >
          <Bell size={16} />
        </button>
        <span style={s.badgeDot} />
      </div> */}

      <div style={s.avatar} title={user?.name || user?.id}>{initial}</div>

      <button
        style={s.iconBtn}
        title="Keluar"
        onClick={logout}
        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.22)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
      >
        <LogOut size={16} />
      </button>
    </header>
  );
}
